// Cross-app end-user SSO bridge (ADR 0038 / ADR 0052). The Identity Portal is the Session Authority:
// it writes the end-user ID token into one cookie scoped to the parent domain `.polux.weapps.net`, and
// every other app (store, grow, …) adopts it read-only. On localhost / preview hosts we use a
// host-only cookie. This holds the ID token only — the token every API Gateway authorizer accepts.

const BRIDGE_COOKIE = 'wa_enduser_idtoken'

/** Prod SSO host (*.polux.weapps.net) → share across the parent domain over HTTPS. */
function isProdSsoHost(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  return host === 'polux.weapps.net' || host.endsWith('.polux.weapps.net')
}

/**
 * Cookie attributes for the bridge cookie. Prod shares it across `.polux.weapps.net` and marks it Secure.
 * Dev (localhost OR a LAN IP for on-device testing) writes a **host-only, non-Secure** cookie: a
 * `Domain=<ip>` attribute is invalid and would be dropped, and a `Secure` cookie won't store over plain
 * HTTP — while host-only means sibling dev servers on the same host still share it (cookies are host- not
 * port-scoped). Must match between the writer (accounts) and the readers (every runtime app).
 */
function bridgeCookieAttrs(): string {
  return isProdSsoHost() ? '; Domain=.polux.weapps.net; Secure' : ''
}

export function isJwtExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now()
  } catch {
    return true
  }
}

export function writeSsoIdToken(idToken: string) {
  if (typeof document === 'undefined' || !idToken) return
  const maxAge = 30 * 24 * 60 * 60 // 30-day max; the token's own exp bounds real validity.
  document.cookie = `${BRIDGE_COOKIE}=${encodeURIComponent(idToken)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${bridgeCookieAttrs()}`
}

export function readSsoIdToken(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${BRIDGE_COOKIE}=`))
  if (!match) return null
  const token = decodeURIComponent(match.slice(BRIDGE_COOKIE.length + 1))
  if (!token || isJwtExpired(token)) return null
  return token
}

export function clearSsoIdToken() {
  if (typeof document === 'undefined') return
  document.cookie = `${BRIDGE_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${bridgeCookieAttrs()}`
}
